import React, { useEffect, useState } from 'react';

import { EnvironmentProvider } from '../../contexts/EnvironmentContext';
import SellerInfo from './components/SellerInfo/SellerInfo';
import SellerContactOptions from './components/SellerContactOptions/SellerContactOptions';
import { TListingClient, TListingSellerInfo } from 'types/TListing';
import { getHeroImageUrl } from '../../utils/getImageUrls';
import { TSellerProfileClient } from 'types/TSellerProfile';
import getSellerProfile from './repositories/getSellerProfile';
import { trackGAEvent } from '../../utils/gaTracking';
import { GA_TRACK_EVENTS_ACTIONS, GA_PARAMETER } from '../../utils/constants';
import createCustomDimension from '../../utils/createCustomDimension';
import { calculateActiveYears } from './components/SellerInfo/utils/calculateActiveSinceDiff';

type TSellerSidebarAppProps = {
  appState: {
    listing: {
      itemId: TListingClient['itemId'];
      title: TListingClient['title'];
      gallery: TListingClient['gallery'];
      priceInfo: TListingClient['priceInfo'];
      seller: TListingSellerInfo;
      flags: TListingClient['flags'];
      traits: TListingClient['traits'];
      isCarAd: TListingClient['isCarAd'];
      isAutomotiveAd: TListingClient['isAutomotiveAd'];
      isCaravansAndCampingAd: TListingClient['isCaravansAndCampingAd'];
      lowBidDetection?: TListingClient['lowBidDetection'];
    };
  };
  environment: any;
};

const SellerSidebarApp = ({ appState, environment }: TSellerSidebarAppProps) => {
  const [profile, setProfile] = useState<TSellerProfileClient>({});
  const {
    listing: {
      itemId,
      title,
      gallery,
      priceInfo,
      seller,
      isCarAd,
      isAutomotiveAd,
      isCaravansAndCampingAd,
      traits,
      flags: { isAdmarkt, hasCallTracking, allowTradeInRequest, isInBinExcludedCategories },
      lowBidDetection,
    },
  } = appState;
  const showExtendedAsq = traits.includes('AUTOMOTIVE_EXTENDED_ASQ');

  useEffect(() => {
    (async () => {
      setProfile(
        await getSellerProfile({
          sellerId: seller.id,
          requestExternalReviews: seller.requestExternalReviews,
          showExtendedAsq,
        }),
      );
    })();
  }, [seller.id, seller.requestExternalReviews, showExtendedAsq]);

  const hasTrackedSellerInfo = React.useRef(false);

  useEffect(() => {
    if (profile && !hasTrackedSellerInfo.current) {
      const { bankAccount, identification, paymentMethod, averageScore, numberOfReviews } = profile;

      if (bankAccount || identification || paymentMethod || averageScore || numberOfReviews) {
        trackGAEvent(GA_TRACK_EVENTS_ACTIONS.SELLER_INFO_AVAILABLE, '', {
          ...createCustomDimension({
            dimension: GA_PARAMETER.SELLER_INFO,
            payload: JSON.stringify({
              YearsActive: calculateActiveYears(seller.activeSince),
              ...(bankAccount && { BankAccount: bankAccount }),
              ...(identification && { IDChecked: identification }),
              ...(paymentMethod && { IdealPayment: paymentMethod?.name === 'ideal' }),
              StarRating: averageScore,
              NumberReviews: numberOfReviews,
            }),
          }),
        });

        hasTrackedSellerInfo.current = true;
      }
    }
  }, [profile, seller.activeSince]);

  return (
    <EnvironmentProvider value={environment}>
      <SellerInfo seller={seller} isCarAd={isCarAd} profile={profile} />
      <SellerContactOptions
        itemId={itemId}
        title={title}
        adImageThumbnail={getHeroImageUrl(gallery.imageUrls[0])}
        priceInfo={priceInfo}
        seller={seller}
        isCarAd={isCarAd}
        isAutomotiveAd={isAutomotiveAd}
        isCaravansAndCampingAd={isCaravansAndCampingAd}
        allowTradeInRequest={allowTradeInRequest}
        isAdmarkt={isAdmarkt}
        hasCallTracking={hasCallTracking}
        isInBinExcludedCategories={isInBinExcludedCategories}
        traits={traits}
        lowBidDetection={lowBidDetection}
        profile={profile}
      />
    </EnvironmentProvider>
  );
};

export default SellerSidebarApp;
